import isEmpty from 'lodash/isEmpty'

import { FILE_ACCEPT_TYPES, FILE_VALIDATION_ERRORS } from '../constants/file'

/**
 * @param {number} length
 * @return {function(ArrayLike): boolean}
 */
export function hasFileListLengthEqualTo(length) {
    return items => {
        return items.length === length
    }
}

/**
 * Create check to identify if the size of one or more files is above a maximum size. Accepts only file lists in the
 * returning function - no DataTransferItemList (has no info about size).
 *
 * @param {number} [maxSize] - Maximum file size for check in bytes
 * @return {function(FileList): boolean}
 */
export function hasFilesSmallerSizeThen(maxSize) {
    return fileList => {
        if (maxSize) {
            return !!Array.from(fileList).every(({ size }) => size <= maxSize)
        }

        return true
    }
}

/**
 * Creates a check to identify if the extension of one or more file is invalid.
 *
 * @param {string} accept - The extensions list of accepted files (f.e. ".mp4", ".jpg,.html")
 * @return {function(FileList|DataTransferItemList): boolean}
 */
export function hasFilesExtensionLike(accept) {
    return items => {
        if (items.length > 0 && accept && accept.length) {
            const extensions = accept.replace(/\s+/g, '').split(',')
            const files = Array.from(items)
            if (
                !files.every(({ name }) => {
                    if (!name) {
                        return false
                    }
                    const extension = '.' + name.split('.').slice(-1).toString().toLowerCase()
                    return extensions.includes(extension.toString().toLowerCase())
                })
            ) {
                return false
            }
        }
        return true
    }
}

/**
 * Creates a check to identify if the mime type of one or more file is invalid.
 *
 * @param {string} [accept] - The mime type expression of accepted files (f.e. "font/*", "image/jpg", "image/*,text/html")
 * @return {function(FileList|DataTransferItemList): boolean}
 */
export function hasFilesMimeTypeLike(accept) {
    return items => {
        if (items.length > 0 && accept && accept.length) {
            const types = accept
                .replace(/\s+/g, '')
                .split(',')
                .reduce((types, type) => {
                    const [base, detail] = type.split('/')
                    types[base] = [detail].concat(types[base])
                    return types
                }, {})
            const files = Array.from(items)
            if (
                !files.every(({ type }) => {
                    const [base, detail] = type.split('/')
                    return types[base] && (types[base].includes('*') || types[base].includes(detail))
                })
            ) {
                return false
            }
        }

        return true
    }
}

/**
 * Creates a check to identify if the mime type of all files is given.
 *
 * @return {function(FileList|DataTransferItemList): boolean}
 */
export function hasFilesWithFilledType() {
    return items => {
        if (items.length > 0) {
            const files = Array.from(items)
            if (!files.every(({ type }) => !isEmpty(type))) {
                return false
            }
        }

        return true
    }
}

/**
 * @param {function(ArrayLike): boolean} check
 * @return {function(ArrayLike): boolean}
 */
export function invertCheck(check) {
    return items => {
        return !check(items)
    }
}

/**
 * @typedef {(function(FileList): FileList|string)} FilesAssertion
 */

/**
 *
 * @param {FilesValidation} assertionParams
 * @return {FilesAssertion}
 */
export const createFileListAssertion = ([check, error]) => {
    return fileList => {
        return check(fileList) ? fileList : error
    }
}

/**
 *
 * @param {FilesValidation[]} assertionEntries
 * @return {FilesAssertion[]}
 */
export const createFileListAssertions = assertionEntries => {
    return assertionEntries.map(createFileListAssertion)
}

/**
 * @param {FileList|DataTransferItemList} items
 * @param {FilesValidation[]} fileValidations
 * @return {string[]}
 */
export const getFileItemsErrors = (items, fileValidations) => {
    return createFileListAssertions(fileValidations).reduce((prev, current) => {
        const result = current(items)
        return typeof result === 'string' ? prev.concat([result]) : prev
    }, [])
}

/**
 * @param {string} acceptedFiles
 * @param {string} [acceptedFilesType]
 * @return {FilesValidation[]}
 */
const getDefaultFileTypeValidations = (acceptedFiles, acceptedFilesType = FILE_ACCEPT_TYPES.MIME_TYPES) => {
    return acceptedFilesType === FILE_ACCEPT_TYPES.EXTENSIONS
        ? [[hasFilesExtensionLike(acceptedFiles), FILE_VALIDATION_ERRORS.fileWithWrongType]]
        : [
              [hasFilesWithFilledType(), FILE_VALIDATION_ERRORS.fileWithWrongType],
              [hasFilesMimeTypeLike(acceptedFiles), FILE_VALIDATION_ERRORS.fileWithWrongType]
          ]
}

/**
 * @param {boolean} [multipleFilesAllowed]
 * @param {number} [maxFileSize]
 * @param {string} [acceptedFiles]
 * @param {string} [acceptedFilesType]
 * @return {FilesValidation[]}
 */
export const getDefaultFileSelectValidations = (
    multipleFilesAllowed,
    maxFileSize,
    acceptedFiles,
    acceptedFilesType
) => {
    return [
        multipleFilesAllowed
            ? [invertCheck(hasFileListLengthEqualTo(0)), FILE_VALIDATION_ERRORS.oneOrMoreFilesNeeded]
            : [hasFileListLengthEqualTo(1), FILE_VALIDATION_ERRORS.exactlyOneFileNeeded],
        [hasFilesSmallerSizeThen(maxFileSize), FILE_VALIDATION_ERRORS.fileSizeToLarge],
        ...getDefaultFileTypeValidations(acceptedFiles, acceptedFilesType)
    ]
}

/**
 * @param {boolean} [multipleFilesAllowed]
 * @param {string} [acceptedFiles]
 * @param {string} [acceptedFilesType]
 * @return {FilesValidation[]}
 */
export const getDefaultDragValidations = multipleFilesAllowed => {
    return [
        multipleFilesAllowed
            ? [invertCheck(hasFileListLengthEqualTo(0)), FILE_VALIDATION_ERRORS.oneOrMoreFilesNeeded]
            : [hasFileListLengthEqualTo(1), FILE_VALIDATION_ERRORS.exactlyOneFileNeeded]
    ]
}
